/**
 * locationService — the location owner for the Map view's Locate button (and its
 * follow mode / blue dot) and for `distanceFromMe`.
 *
 * SCOPE, honestly stated: this is NOT yet the app's only caller of
 * `navigator.geolocation`. 15 other source files still call the browser API
 * directly and therefore bypass everything below — the single-flight share, the
 * cache, the app-side hard timeout, the always-explicit `PositionOptions`, the
 * diagnostics ring buffer and the blue-dot notifications. The main ones:
 *   - helpers/navigation.ts
 *   - Modules/PractitionerBaseballCard (index + Details + Details/QuickFill)
 *   - Modules/PractitionerBaseballCard/.../CheckIn/.../CheckInForm
 *   - Modules/CreateLead (index + LocationSearch)
 *   - components/LeadDrawer (LeadSteps + LocationSearch)
 *   - the MapRouter CurrentRoute/ViewRoute SearchModal copies, duplicated under
 *     both Modules/MapRouter and components/RoutesDrawer/MapRouter
 * Migrating them is a deliberate follow-up ticket, not part of EIS-1089. Do not
 * restate this module as the sole owner until that ticket lands.
 * (`grep -rl "navigator\.geolocation" src/` is the check.)
 *
 * Why this exists (EIS-1089):
 *  - mapbox-gl's `GeolocateControl` shallow-merges `positionOptions`, so passing
 *    `{ enableHighAccuracy: true }` silently DELETED the library's default
 *    `timeout: 6000`. The result was a high-accuracy, never-cached, never-timing-out
 *    request that hangs forever indoors and never invokes the error callback.
 *  - `GeolocateControl` also keeps latchable internal state (`_numberOfWatches`,
 *    `_noTimeout`) that is only reset in its constructor, i.e. only on page load.
 *    Once latched, the Locate button became a permanent silent no-op — fatal in a
 *    standalone iOS PWA, which has no reload affordance.
 *  - Every list row independently called `getCurrentPosition`, producing up to 40
 *    concurrent uncached, untimed browser requests.
 *
 * The rules this module enforces:
 *  1. ALWAYS pass an explicit `timeout` AND `maximumAge`. Never rely on defaults.
 *  2. Race an app-side `setTimeout` ON TOP of `PositionOptions.timeout`, because
 *     iOS can fail to invoke either callback at all.
 *  3. NO latchable state. A failure may only release the in-flight slot; it must
 *     never set a flag that degrades or disables subsequent calls.
 *  4. Single in-flight request shared by all concurrent callers — with ONE
 *     deliberate exception: a caller that passes `gesture: true` never
 *     piggybacks on somebody else's pending request (see rule 5).
 *  5. `navigator.geolocation.getCurrentPosition` is invoked SYNCHRONOUSLY inside
 *     the caller's call stack whenever this module reaches the browser at all,
 *     and a `gesture: true` caller ALWAYS reaches the browser. iOS ties the
 *     permission prompt to the transient user activation of the originating tap,
 *     so a tap that merely inherited another caller's promise would never have
 *     issued a call under its own activation — and the prompt can then silently
 *     fail to appear (see `getPosition`).
 *  6. The continuous watch (`startWatch`/`stopWatch`) is PURELY ADDITIVE. The
 *     gesture-bound one-shot `getCurrentPosition` is the load-bearing path and
 *     never routes through the watch, because `watchPosition` is one of the
 *     primitives suspected of never invoking either callback in an iOS
 *     standalone PWA. A watch that stays silent forever must leave the app in
 *     exactly the state it would be in with no watch at all (see `startWatch`).
 */

export type GeoErrorKind =
  | 'DENIED'
  | 'UNAVAILABLE'
  | 'TIMEOUT'
  | 'UNSUPPORTED'
  | 'INSECURE';

export interface GeoPosition {
  lat: number;
  lon: number;
  accuracy: number;
  /** ms epoch of the fix */
  timestamp: number;
}

export interface GeoFailure {
  kind: GeoErrorKind;
  /** the raw `GeolocationPositionError.code`, or null when we failed before calling out */
  code: number | null;
  message: string;
}

export interface GetPositionOptions {
  /** how old a cached fix may be and still be reused. 0 (default) = always refetch */
  maxAgeMs?: number;
  /** app-side hard timeout; also forwarded as `PositionOptions.timeout` */
  timeoutMs?: number;
  highAccuracy?: boolean;
  /** free-form label recorded in the diagnostics ring buffer, e.g. 'locate' | 'distance' */
  source?: string;
  /**
   * this request originates from a user gesture; it must issue its own browser
   * call so iOS's transient user activation is preserved.
   *
   * Set it ONLY from inside a real event handler (the Locate button's click).
   * A gesture caller still respects the cache normally — passing `maxAgeMs: 0`,
   * as the Locate button does, already misses the cache.
   */
  gesture?: boolean;
}

export interface StartWatchOptions {
  /** forwarded as `PositionOptions.timeout` */
  timeoutMs?: number;
  /** forwarded as `PositionOptions.maximumAge` */
  maxAgeMs?: number;
  highAccuracy?: boolean;
  /** free-form label recorded in the diagnostics ring buffer */
  source?: string;
}

export interface GeoDiagnosticEntry {
  t: number;
  source: string;
  durationMs: number;
  ok: boolean;
  code: number | null;
  message: string;
  accuracy: number | null;
}

export interface GeoDiagnostics {
  entries: GeoDiagnosticEntry[];
  lastPosition: GeoPosition | null;
  lastPositionAgeMs: number | null;
  hasGeolocation: boolean;
  isSecureContext: boolean;
  inFlight: boolean;
  subscriberCount: number;
  /** an OS watch is currently registered (it may still never call back) */
  watching: boolean;
}

export const DEFAULT_TIMEOUT_MS = 10000;
/** watch budget: generous, because a silent watch costs the user nothing */
export const DEFAULT_WATCH_TIMEOUT_MS = 15000;
/** a fix older than this is dropped when the app is resumed from background */
export const STALE_ON_RESUME_MS = 5 * 60 * 1000;
const RING_BUFFER_SIZE = 20;
/**
 * The `message` written for an entry that never reached the browser because a
 * fresh-enough cached fix was reused. It is a named constant because
 * `isLocationDenied` has to recognise those entries and skip them; two copies of
 * the literal would let the recording side drift away from the reading side.
 */
const CACHE_HIT_MESSAGE = 'cache hit';
// Never reaches the ring buffer (see `makeWatchSuccessHandler`); it exists so
// the verdict-only entry is self-describing if it is ever inspected.
const WATCH_FIX_MESSAGE = 'watch fix';
const DIAGNOSTICS_STORAGE_KEY = 'eis.geo.diagnostics.v1';

/** GeolocationPositionError codes (not exposed as constants in every browser). */
const CODE_PERMISSION_DENIED = 1;
const CODE_POSITION_UNAVAILABLE = 2;
const CODE_TIMEOUT = 3;

export class GeoError extends Error implements GeoFailure {
  public readonly kind: GeoErrorKind;

  public readonly code: number | null;

  constructor(kind: GeoErrorKind, code: number | null, message: string) {
    super(message);
    this.name = 'GeoError';
    this.kind = kind;
    this.code = code;
    // keeps `instanceof` working across transpilation targets
    Object.setPrototypeOf(this, GeoError.prototype);
  }
}

type Subscriber = { fn: (p: GeoPosition | null) => void };

/**
 * A slot rather than a bare promise: the slot object is published to module scope
 * BEFORE the Promise executor runs, so a geolocation callback that fires
 * synchronously (as mocks do) can still correctly release the slot.
 */
type InFlightSlot = { promise?: Promise<GeoPosition> };

// ---------------------------------------------------------------------------
// module state. NOTE: nothing here may ever act as a "give up" latch.
// ---------------------------------------------------------------------------
let cachedPosition: GeoPosition | null = null;
let inFlight: InFlightSlot | null = null;
let subscribers: Subscriber[] = [];
let ringBuffer: GeoDiagnosticEntry[] = [];
let lifecycleAttached = false;
/**
 * THE DENIAL VERDICT'S ONLY EVIDENCE: the newest attempt that actually called
 * out to the browser, held in memory and NEVER persisted. Read by
 * `isLocationDenied` and `getInferredDenialAt`.
 *
 * WHO WRITES IT, exhaustively — the list is short on purpose, because an
 * asymmetry here is a bug (round 5 fixed one: the watch could convict but not
 * acquit):
 *  - `record`, for every entry except a cache hit. That is every `getPosition`
 *    outcome (success, browser error, app-side timeout, the UNSUPPORTED and
 *    INSECURE guards, a synchronous throw) and both watch FAILURE paths (an
 *    asynchronous watch error, and a `watchPosition` that throws synchronously).
 *  - `recordVerdictOnly`, from a successful watch tick and nowhere else — the
 *    one path that must vote without consuming a ring-buffer slot.
 *  - `__resetForTests`, which nulls it. Test hygiene, not a page load.
 *
 * WHO DELIBERATELY DOES NOT:
 *  - a cache hit. It never touched the browser, so it is no evidence about
 *    permission NOW (see `isCacheHit`, and test 17f).
 *  - `loadRingBuffer`. Restored entries are DISPLAY history from a previous
 *    page session: fully visible, never voting. See below.
 *  - `getPosition`'s shared-in-flight early return. It issues no browser call of
 *    its own; the request it joins records on behalf of both callers.
 *  - `startWatch`'s idempotence guard, `stopWatch`, `invalidateCache`, `notify`
 *    and the lifecycle listeners. None of them learn anything new about
 *    permission, and writing a verdict from any of them would be the "give up"
 *    latch class this module exists to eliminate.
 *
 * It is a separate variable from `ringBuffer` on purpose. The two look like the
 * same information but have opposite requirements, and rounds 1–3 conflated
 * them:
 *
 *  - the ring buffer is DISPLAY. It is capped at RING_BUFFER_SIZE and persisted
 *    so support can read a rep's recent history — including history from before
 *    the current page load.
 *  - the verdict needs AUTHORITATIVE, SESSION-SCOPED state. It must survive
 *    eviction and must not survive a page load.
 *
 * Scanning the buffer for the verdict failed both ways round:
 *
 *  - EVICTION (round-3 defect). `getPosition` records a `'cache hit'` entry for
 *    every caller, and `distanceFromMe` is called once per list row — up to 40.
 *    One list-view toggle with a warm fix therefore pushed ~40 entries through a
 *    20-entry ring and evicted the denial completely, even though the scan
 *    deliberately skipped cache hits: skipping an entry does not stop it
 *    consuming a slot. Deny -> open list view -> long-press Locate showed NO
 *    recovery block, which is the one thing this ticket exists to deliver. A
 *    variable cannot be evicted, so the size of the ring is now irrelevant to
 *    the verdict. (Pinned by test 17q.)
 *  - THE WALL CLOCK (round-3 defect). Session membership used to be
 *    `entry.t >= sessionStartedAt`, comparing persisted epoch timestamps against
 *    a boundary stamped at module init. A device clock that moves BACKWARD
 *    between a denial and a reload leaves the restored denial future-dated
 *    relative to the new boundary, so it still convicts; a clock that moves
 *    backward during a live page excludes fresh attempts instead. Stamping
 *    before rather than after `loadRingBuffer()` fixes neither case, so the
 *    "ORDER MATTERS" comment that used to sit at module init was inaccurate as
 *    well as load-bearing-looking. Session membership is now STRUCTURAL: this
 *    variable is null on every module instantiation, i.e. on every page load,
 *    and restored entries are never written into it because they never pass
 *    through `record`. No timestamp is compared, so no clock can be wrong.
 *    (Pinned by tests 17m and 17s.)
 */
let lastRealAttempt: GeoDiagnosticEntry | null = null;
/** the id returned by `watchPosition`, or null when no watch is registered */
let watchId: number | null = null;

// ---------------------------------------------------------------------------
// diagnostics ring buffer (persisted; Safari private mode throws on localStorage)
// ---------------------------------------------------------------------------
const loadRingBuffer = (): void => {
  try {
    const raw = window.localStorage.getItem(DIAGNOSTICS_STORAGE_KEY);
    if (!raw) return;
    const parsed = JSON.parse(raw);
    if (Array.isArray(parsed)) {
      ringBuffer = parsed.slice(-RING_BUFFER_SIZE);
    }
  } catch {
    // private mode / disabled storage / corrupt JSON — diagnostics are best effort
  }
};

const persistRingBuffer = (): void => {
  try {
    window.localStorage.setItem(
      DIAGNOSTICS_STORAGE_KEY,
      JSON.stringify(ringBuffer)
    );
  } catch {
    // best effort only
  }
};

/**
 * An entry that never reached the browser because a fresh-enough cached fix was
 * reused. It proves only that an OLD fix is still young, so it is no evidence
 * about permission NOW and must never vote — see `isLocationDenied`.
 */
const isCacheHit = (e: GeoDiagnosticEntry): boolean =>
  e.ok && e.message === CACHE_HIT_MESSAGE;

/**
 * The writer for BOTH halves of the diagnostics state, so the display copy and
 * the verdict copy can never drift apart the way independent call sites would.
 * Every attempt a user could later need explained goes through here.
 */
const record = (entry: GeoDiagnosticEntry): void => {
  // display half: capped and persisted, and therefore evictable
  ringBuffer.push(entry);
  if (ringBuffer.length > RING_BUFFER_SIZE) {
    ringBuffer = ringBuffer.slice(-RING_BUFFER_SIZE);
  }
  persistRingBuffer();
  // verdict half: uncapped, unpersisted, and therefore un-evictable
  if (!isCacheHit(entry)) lastRealAttempt = entry;
};

/**
 * The VERDICT half ONLY: newest real browser evidence, no ring-buffer row. The
 * single caller is a successful watch tick.
 *
 * This is only expressible because round 4 split the two halves apart. While
 * the ring buffer WAS the verdict, "should this vote?" and "should this be
 * displayed?" were the same question, and a walking user's tick stream is the
 * one case whose honest answers differ: it is real browser evidence (so it must
 * vote) and it arrives every few seconds (so it must not consume ring slots).
 * See `makeWatchSuccessHandler` for the defect that required it.
 */
const recordVerdictOnly = (entry: GeoDiagnosticEntry): void => {
  lastRealAttempt = entry;
};

// ---------------------------------------------------------------------------
// subscribers
// ---------------------------------------------------------------------------
const notify = (p: GeoPosition | null): void => {
  // copy first: a subscriber may unsubscribe from inside its own callback
  subscribers.slice().forEach((s) => {
    try {
      s.fn(p);
    } catch {
      // a broken subscriber must not break the location pipeline
    }
  });
};

/**
 * Subscribe to position updates (used by the map's blue dot).
 * The callback fires immediately with the currently known position (or null),
 * then on every subsequent fix or invalidation.
 *
 * Safe under React StrictMode double-invocation: each call registers its own
 * record and the returned unsubscribe removes only that record, and is a no-op
 * if called more than once.
 */
export const subscribe = (
  fn: (p: GeoPosition | null) => void
): (() => void) => {
  const entry: Subscriber = { fn };
  subscribers.push(entry);
  try {
    fn(cachedPosition);
  } catch {
    // ignore
  }
  return () => {
    const i = subscribers.indexOf(entry);
    if (i >= 0) subscribers.splice(i, 1);
  };
};

// ---------------------------------------------------------------------------
// cache
// ---------------------------------------------------------------------------
/**
 * Returns the last known fix if it is younger than `maxAgeMs`.
 * `maxAgeMs` of 0 (the default) always misses — callers who ask for 0 want a
 * genuinely fresh fix.
 */
export const getCached = (maxAgeMs = 0): GeoPosition | null => {
  if (!cachedPosition) return null;
  if (maxAgeMs <= 0) return null;
  const age = Date.now() - cachedPosition.timestamp;
  if (age < 0 || age > maxAgeMs) return null;
  return cachedPosition;
};

/** Drop the cached fix (used on resume when it is too stale to trust). */
export const invalidateCache = (): void => {
  if (cachedPosition === null) return;
  cachedPosition = null;
  notify(null);
};

// ---------------------------------------------------------------------------
// error mapping / copy
// ---------------------------------------------------------------------------
const kindFromCode = (code: number | null | undefined): GeoErrorKind => {
  switch (code) {
    case CODE_PERMISSION_DENIED:
      return 'DENIED';
    case CODE_POSITION_UNAVAILABLE:
      return 'UNAVAILABLE';
    case CODE_TIMEOUT:
      return 'TIMEOUT';
    default:
      return 'UNAVAILABLE';
  }
};

/**
 * True when the app is running as an INSTALLED (home-screen) PWA rather than in
 * a Safari tab.
 *
 * Exported because the DENIED copy has to differ between the two: on iOS 16.4+
 * an installed PWA gets its OWN entry in Settings → Privacy & Security →
 * Location Services, while "Safari Websites" governs Safari's tabs — so that
 * row is the wrong place to send a standalone user. `LocationDebugPanel` reuses
 * this instead of re-deriving it, so the toast and the panel can never disagree
 * about which instructions the user is being handed.
 *
 * Both signals are read defensively: Safari has been inconsistent about
 * `matchMedia` support for `display-mode` queries, and `navigator.standalone` is
 * a non-standard WebKit property. Failing to detect standalone mode must degrade
 * to the browser copy, never throw inside an error path.
 */
export const isStandalone = (): boolean => {
  try {
    if (window.matchMedia?.('(display-mode: standalone)')?.matches) return true;
  } catch {
    // matchMedia missing, or throwing on an unsupported feature query
  }
  try {
    if ((navigator as any)?.standalone === true) return true;
  } catch {
    // non-standard property — reading it must never break the failure copy
  }
  return false;
};

export interface DescribeGeoFailureOptions {
  /**
   * Override the runtime standalone detection. Application code never passes
   * this; it exists so tests can drive both branches deterministically.
   */
  standalone?: boolean;
}

/**
 * User-facing copy for a failure. Only the DENIED case is context-aware; every
 * other kind reads identically in a tab and in an installed PWA.
 */
export const describeGeoFailure = (
  f: GeoFailure,
  opts: DescribeGeoFailureOptions = {}
): string => {
  switch (f?.kind) {
    case 'DENIED':
      // UAT of EIS-1089 found this copy, not the fix, to be the remaining
      // defect. The old single string sent an installed-PWA user to "Safari
      // Websites" (Safari's entry, not the app's) and promised that closing and
      // reopening would bring the prompt back. It does not: WebKit persists the
      // denial for the origin, so `getCurrentPosition` fails immediately with no
      // prompt, and deleting and re-adding the app does NOT clear it. Only the
      // Settings toggle does. That one sentence cost the tester two full
      // delete-and-reinstall cycles, so the standalone string now says outright
      // that reinstalling will not restore it.
      //
      // Both strings are deliberately SHORT (<= 180 chars, pinned by test 6c):
      // this is a toast over the map on a phone, and the previous 230-character
      // version already covered roughly a third of the screen. The full
      // step-by-step recovery lives in LocationDebugPanel — which is also why
      // the standalone string points at the long-press, otherwise undiscoverable.
      //
      // Neither string names the Location Services ROW to tap: we have not
      // confirmed on-device whether an installed PWA is listed under its own
      // name or folded under "Safari Websites", so the copy points at the
      // SCREEN and lets the user find the entry. The panel, which has room,
      // names both candidates and says to check both.
      return (opts.standalone ?? isStandalone())
        ? "Location is blocked for this app. Reinstalling won't restore it. Open Settings → Privacy & Security → Location Services and allow this app. Long-press Locate for steps."
        : 'Location is blocked for this site. Open Settings → Privacy & Security → Location Services → Safari Websites, then Settings → Safari → Location and allow this site.';
    case 'UNAVAILABLE':
      return "Couldn't get a location fix. Move near a window or outdoors and try again.";
    case 'TIMEOUT':
      return 'Location is taking too long. Tap Locate again, or move somewhere with a clearer view of the sky.';
    case 'UNSUPPORTED':
      return "This device or browser can't provide location.";
    case 'INSECURE':
      return 'Location needs a secure (https) connection.';
    default:
      return "Couldn't get a location fix. Move near a window or outdoors and try again.";
  }
};

const MINUTE_MS = 60 * 1000;
const HOUR_MS = 60 * MINUTE_MS;
const DAY_MS = 24 * HOUR_MS;

/**
 * How old the evidence behind an inferred denial is, in words.
 *
 * WHY THIS EXISTS. The panel used to disclose the evidence with
 * `toLocaleTimeString()` alone — a bare time of day. But the case this
 * disclosure was written FOR is explicitly a multi-day one: iOS keeps a
 * standalone PWA's page alive for days, so the denial being reported can have
 * been recorded on Monday and read on Tuesday, where "at 14:32:05" reads as
 * today. That is worse than no disclosure, because it invites the reader to
 * conclude the evidence is current.
 *
 * WHY RELATIVE AGE RATHER THAN A DATE. The reader is a rep in the field, and the
 * only question they are answering is "was this recorded before or after I
 * changed the Settings toggle?". "3 days ago" answers that directly; a date
 * makes them do the arithmetic. The panel prints the absolute stamp next to it
 * anyway (`toLocaleString()`, date AND time) for support, who need to line the
 * disclosure up against the ring-buffer table — so nothing is lost.
 *
 * `now` is injectable so the thresholds are testable without faking the clock.
 * A NEGATIVE age (the device clock moved backwards between recording and
 * rendering) falls into the first branch and reads "just now", which is the
 * honest answer for evidence that cannot be placed in the past.
 *
 * This lives in the service rather than the panel for the same reason
 * `describeGeoFailure` does: `LocationDebugPanel` cannot be rendered under this
 * Jest config, so user-facing copy left in the component is untestable — which
 * is how the round-1 defect reached review.
 */
export const describeDenialEvidenceAge = (
  t: number,
  now: number = Date.now()
): string => {
  const ageMs = now - t;
  const plural = (n: number, unit: string) =>
    `${n} ${unit}${n === 1 ? '' : 's'} ago`;
  if (ageMs < MINUTE_MS) return 'just now';
  if (ageMs < HOUR_MS) return plural(Math.floor(ageMs / MINUTE_MS), 'minute');
  if (ageMs < DAY_MS) return plural(Math.floor(ageMs / HOUR_MS), 'hour');
  return plural(Math.floor(ageMs / DAY_MS), 'day');
};

const MONTHS = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
];

const pad2 = (n: number): string => String(n).padStart(2, '0');

/**
 * One row's timestamp in the panel's "Recent attempts" table, e.g.
 * `25 Aug 14:32:05`.
 *
 * WHY THIS EXISTS. The table used to render `toLocaleTimeString()` — a time of
 * day and nothing else — which is the same date-omission defect as the
 * disclosure above, and sharper here: this table is exactly where entries
 * RESTORED FROM A PREVIOUS PAGE SESSION surface. `loadRingBuffer` deliberately
 * keeps them visible because support needs the history, and `lastRealAttempt`
 * deliberately refuses to let them vote — but a support reader looking at a
 * time-only column could not tell Monday's denial from this morning's, which is
 * precisely the distinction the round-4 verdict change turns on.
 *
 * ABSOLUTE AND COMPACT, not the prose age used by `describeDenialEvidenceAge`.
 * The two readers want different things. The disclosure has ONE timestamp and a
 * field rep asking "before or after I changed the toggle?", so prose wins. The
 * table has up to RING_BUFFER_SIZE rows in a narrow modal column on a phone, and
 * its reader is support correlating rows against each other and against server
 * logs: "3 days ago" x20 is unscannable and cannot be compared between rows,
 * whereas a fixed-width stamp sorts and diffs by eye. Day + short month is
 * enough — the buffer holds at most a few sessions, so the year is noise.
 *
 * Hand-formatted rather than `toLocaleString()` so the output is deterministic
 * and can be asserted exactly: the panel cannot be rendered under this Jest
 * config, so anything left to the component is untested. LOCAL time throughout
 * (`getDate`/`getMonth`/`getHours`), matching what the column showed before, so
 * no reader sees a timezone shift and the tests are timezone-independent.
 */
export const formatDiagnosticTimestamp = (t: number): string => {
  const d = new Date(t);
  return `${d.getDate()} ${MONTHS[d.getMonth()]} ${pad2(d.getHours())}:${pad2(
    d.getMinutes()
  )}:${pad2(d.getSeconds())}`;
};

/**
 * Should the app show the "location is blocked, here is how to fix it" recovery
 * path?
 *
 * WHY THIS IS NOT `permissionState === 'denied'` (the round-1 UAT defect):
 * `LocationDebugPanel.readEnvironment` reads `navigator.permissions.query({name:
 * 'geolocation'})`, and its own code anticipates that call being MISSING
 * (`'unsupported'`) or REJECTING (`` `error: ...` ``) — because Safari's support
 * for the Permissions API is inconsistent. iOS, the exact platform this ticket
 * exists for, is where that is most likely. Gating the recovery steps on the
 * literal string `'denied'` therefore hid them precisely on the device whose
 * user was being told (by the DENIED toast) to long-press Locate to find them.
 *
 * The RECORDED ATTEMPTS are the reliable ground truth: `getPosition` records
 * every attempt with the raw `GeolocationPositionError.code`, and code 1 is
 * PERMISSION_DENIED. That signal exists whether or not `navigator.permissions`
 * does.
 *
 * The rules, in order:
 *  - `'denied'`               -> true. The Permissions API is authoritative when
 *                                it actually answers.
 *  - `'granted'` / `'prompt'` -> false, likewise. A recorded denial that the
 *                                user has since cleared must not nag.
 *  - anything else (`'unknown'`, `'unsupported'`, `` `error: ...` ``,
 *    `undefined`) -> the API told us nothing, so fall back to the most recent
 *    RECORDED ATTEMPT:
 *      * no attempts recorded   -> false. Nothing has failed yet; do not open
 *                                  with alarming guidance.
 *      * it succeeded           -> false. The user has a real fix, so whatever
 *                                  happened earlier has been resolved.
 *      * it failed with code 1  -> TRUE. This is the iOS case.
 *      * it failed with code 2
 *        (UNAVAILABLE) or 3
 *        (TIMEOUT), or with no
 *        code at all (the
 *        UNSUPPORTED / INSECURE
 *        guards, a synchronous
 *        throw)                 -> false. Real problems, but not permission
 *                                  problems, and the Settings steps would be the
 *                                  wrong remedy.
 *
 * CACHE HITS ARE SKIPPED, not counted as a success. A `'cache hit'` entry
 * (`ok: true`) only proves that a previous fix is still young enough to reuse —
 * it never touched the browser, so it is no evidence at all that permission was
 * re-granted. A user who is denied but whose 5-minute-old fix is still warm
 * would otherwise be shown "no problem here" on the one screen they opened
 * because Locate is failing. So `record` never lets a cache hit overwrite
 * `lastRealAttempt`, and a cache hit sitting on top of a code-1 denial still
 * yields true. (Pinned by test 17f.)
 *
 * WATCH TICKS COUNT IN BOTH DIRECTIONS. `watchPosition` reporting code 1 is the
 * same OS verdict as `getCurrentPosition` reporting it, and a watch tick that
 * DELIVERS a fix is the same acquittal as a successful one-shot. Only the error
 * half leaves a ring-buffer row; see `makeWatchSuccessHandler` for why the
 * success half votes silently, and 17t-17v for the asymmetry that let a watch
 * convict but never acquit.
 *
 * THE EVIDENCE IS `lastRealAttempt`, NOT THE RING BUFFER — see that variable for
 * the two round-3 defects (eviction by 40 cache hits, and a wall-clock session
 * boundary) that scanning the buffer caused. The property that matters here is
 * unchanged and now holds structurally rather than by timestamp arithmetic: a
 * denial from a PREVIOUS page session is not evidence about NOW. The exact case:
 * a user is denied, closes the app, fixes the toggle in Settings, reopens the
 * app and opens this panel before tapping Locate again. Entries restored from
 * localStorage never pass through `record`, so they can never become the
 * verdict's evidence — while staying fully VISIBLE in `getDiagnostics()`,
 * because support still needs the history. They simply do not vote. The
 * in-session half of the same problem is handled by `getInferredDenialAt`
 * below — see its comment for why a recency cap was rejected in favour of
 * disclosure.
 *
 * This lives in the service, not in the panel, ON PURPOSE. `LocationDebugPanel`
 * cannot be rendered under this Jest config (mapbox-gl needs WebGL and the
 * import graph hits the same untransformed axios ESM that breaks App.test.tsx),
 * so any decision left in the component is untestable — which is exactly how the
 * round-1 defect reached review. Here it is covered by the gate.
 */
export const isLocationDenied = (permissionState?: string): boolean => {
  if (permissionState === 'denied') return true;
  if (permissionState === 'granted' || permissionState === 'prompt') {
    return false;
  }

  if (!lastRealAttempt) return false;
  if (lastRealAttempt.ok) return false;
  return lastRealAttempt.code === CODE_PERMISSION_DENIED;
};

/**
 * When the evidence behind an INFERRED denial was observed, or null when the
 * verdict was not inferred (i.e. the Permissions API answered `'denied'` itself,
 * or there is no denial at all).
 *
 * Reads the SAME `lastRealAttempt` the verdict read, so the time we disclose can
 * never be drawn from a different attempt than the one that convicted.
 *
 * This exists because of the in-session staleness sub-case that the page-session
 * scoping above cannot close: iOS keeps a standalone PWA's page alive for days, so
 * a user can be denied, background the app, fix Settings and return to the SAME
 * live page. Their last real attempt is still the denial, so inference still
 * says "blocked" until they tap Locate again.
 *
 * WHY NOT A RECENCY CAP. The obvious alternative — ignore evidence older than N
 * minutes — was rejected because its failure mode is the round-1 defect coming
 * back. A user who is genuinely still denied and has not retried within the
 * window would be shown "no problem here" on the one screen they opened because
 * Locate is failing, and the recovery steps would be hidden from exactly the
 * person who needs them. A stale "still blocked" costs one wasted tap on Locate;
 * a premature "all clear" costs the user the only instructions that fix it. The
 * asymmetry decides it: keep inferring, and DISCLOSE THE TIMESTAMP so the reader
 * can judge the evidence for themselves.
 *
 * WHY NOT REARM ON FOREGROUND. The lifecycle listeners below already fire on
 * `visibilitychange`, and clearing `lastRealAttempt` there would close this
 * sub-case automatically — but it would also break the primary flow. The DENIED
 * toast tells the user to go to Settings, which BACKGROUNDS the app; returning
 * and long-pressing Locate would then find no evidence at all and show them
 * nothing. A foreground stretch is not a page session. Left alone on purpose.
 *
 * WHAT DOES CLOSE IT is real evidence rather than a guess: any attempt that
 * reaches the browser and succeeds overwrites `lastRealAttempt`, so this returns
 * null again with nothing inferred. Since round 5 that includes a fix delivered
 * by the watch which a denial deliberately leaves registered — the recovery path
 * that needs no tap at all.
 */
export const getInferredDenialAt = (permissionState?: string): number | null => {
  // the API answered for itself; the panel labels that as reported, not inferred
  if (permissionState === 'denied') return null;
  if (!isLocationDenied(permissionState)) return null;
  return lastRealAttempt ? lastRealAttempt.t : null;
};

// ---------------------------------------------------------------------------
// getPosition
// ---------------------------------------------------------------------------
const hasGeolocation = (): boolean =>
  typeof navigator !== 'undefined' && 'geolocation' in navigator && !!navigator.geolocation;

const isSecure = (): boolean => {
  if (typeof window === 'undefined') return false;
  // treat "not implemented" as secure rather than blocking the user
  if (typeof window.isSecureContext === 'undefined') return true;
  return window.isSecureContext;
};

/**
 * Get a position.
 *
 * IMPORTANT: when this call reaches the browser at all,
 * `navigator.geolocation.getCurrentPosition` is invoked SYNCHRONOUSLY within the
 * caller's call stack — the cache lookup, the environment guards and the call
 * itself all happen before this function returns, with no `await`, `setTimeout`
 * or microtask hop in between. iOS Safari ties the permission prompt to the
 * transient user activation of the originating tap; any async hop first drops
 * that activation and the prompt is silently never shown.
 *
 * Two things can short-circuit before the browser is reached, and a caller that
 * needs the activation guarantee must understand both:
 *  - a fresh-enough CACHED fix is returned instead (only possible with
 *    `maxAgeMs > 0`; the Locate button passes 0 and so always calls out);
 *  - an already IN-FLIGHT request is shared instead. This is what makes 40 list
 *    rows one browser call — but it is unsafe for a tap, which would then never
 *    have called out under its own activation, and would silently inherit the
 *    other request's accuracy/timeout/maximumAge. Pass `gesture: true` to opt
 *    out of the share; such a caller ALWAYS issues its own call with its own
 *    `PositionOptions`.
 *
 * On failure the ONLY state mutated is the in-flight slot. There is deliberately
 * no "we already failed" flag — that latch is precisely what made the old
 * Locate button a permanent no-op.
 */
export const getPosition = (
  opts: GetPositionOptions = {}
): Promise<GeoPosition> => {
  const {
    maxAgeMs = 0,
    timeoutMs = DEFAULT_TIMEOUT_MS,
    highAccuracy = true,
    source = 'unknown',
    gesture = false,
  } = opts;

  const startedAt = Date.now();

  // 1. cache — synchronous
  const cached = getCached(maxAgeMs);
  if (cached) {
    record({
      t: startedAt,
      source,
      durationMs: 0,
      ok: true,
      code: null,
      message: CACHE_HIT_MESSAGE,
      accuracy: cached.accuracy,
    });
    return Promise.resolve(cached);
  }

  // 2. share the single in-flight browser request — synchronous.
  //    A `gesture` caller deliberately skips this: handing it somebody else's
  //    pending promise would mean the tap never called getCurrentPosition inside
  //    its own call stack, so iOS's transient user activation is gone and the
  //    permission prompt can silently never appear — the exact EIS-1089 failure
  //    class. It would also silently substitute the other request's options
  //    (e.g. the list view's highAccuracy:false / 8s budget) for the tap's own.
  if (!gesture && inFlight && inFlight.promise) {
    return inFlight.promise;
  }

  // 3. environment guards — synchronous
  if (!hasGeolocation()) {
    const err = new GeoError(
      'UNSUPPORTED',
      null,
      'geolocation is not available in this browser'
    );
    record({
      t: startedAt,
      source,
      durationMs: 0,
      ok: false,
      code: null,
      message: err.message,
      accuracy: null,
    });
    return Promise.reject(err);
  }
  if (!isSecure()) {
    const err = new GeoError(
      'INSECURE',
      null,
      'geolocation requires a secure context'
    );
    record({
      t: startedAt,
      source,
      durationMs: 0,
      ok: false,
      code: null,
      message: err.message,
      accuracy: null,
    });
    return Promise.reject(err);
  }

  // Publish the slot BEFORE running the executor so a synchronously-invoked
  // callback still releases it correctly.
  //
  // A `gesture` request publishes itself too, overwriting any older slot
  // (last-writer-wins), so with two requests genuinely in flight only the newest
  // is shareable. That is the deliberate choice: piggybacking is only unsafe in
  // the direction where a TAP inherits someone else's call, because only the tap
  // needs the user activation. A later non-gesture caller (a distance
  // calculation) needs no activation, so letting it share the gesture's fresher
  // high-accuracy request is strictly better than issuing another browser call.
  //
  // Every other piece of per-request state — `settled`, the app-side timer, the
  // resolve/reject pair — lives in the executor's own closure, so the older,
  // now-unpublished request keeps working normally and settles its own callers.
  // Its `releaseSlot` becomes a no-op precisely because `inFlight === slot`
  // fails, which is what stops a request finishing SECOND from nulling out a
  // newer slot and re-splitting the traffic behind it.
  const slot: InFlightSlot = {};
  inFlight = slot;
  const releaseSlot = () => {
    if (inFlight === slot) inFlight = null;
  };

  const promise = new Promise<GeoPosition>((resolve, reject) => {
    let settled = false;
    let timer: ReturnType<typeof setTimeout> | null = null;

    const finish = () => {
      settled = true;
      if (timer !== null) {
        clearTimeout(timer);
        timer = null;
      }
      // the ONLY state a failure is allowed to touch
      releaseSlot();
    };

    // App-side hard timeout ON TOP of PositionOptions.timeout: iOS can fail to
    // invoke either callback at all, in which case the library timeout never
    // fires and the promise would hang forever.
    timer = setTimeout(() => {
      if (settled) return;
      finish();
      const err = new GeoError(
        'TIMEOUT',
        CODE_TIMEOUT,
        `location request exceeded ${timeoutMs}ms`
      );
      record({
        t: startedAt,
        source,
        durationMs: Date.now() - startedAt,
        ok: false,
        code: CODE_TIMEOUT,
        message: err.message,
        accuracy: null,
      });
      reject(err);
    }, timeoutMs);

    const onSuccess = (position: GeolocationPosition) => {
      if (settled) return;
      finish();
      const p: GeoPosition = {
        lat: position.coords.latitude,
        lon: position.coords.longitude,
        accuracy: position.coords.accuracy,
        timestamp: position.timestamp || Date.now(),
      };
      // With two requests in flight the callbacks can land in either order, so
      // only ever ADVANCE the shared cache — never rewind it to an older fix.
      // The caller always gets its own `p` regardless; this guards only the
      // module-wide cache and the blue dot fed from it.
      const isNewerThanCache =
        !cachedPosition || p.timestamp >= cachedPosition.timestamp;
      if (isNewerThanCache) {
        cachedPosition = p;
      }
      record({
        t: startedAt,
        source,
        durationMs: Date.now() - startedAt,
        ok: true,
        code: null,
        message: 'ok',
        accuracy: p.accuracy,
      });
      if (isNewerThanCache) {
        notify(p);
      }
      resolve(p);
    };

    const onError = (error: GeolocationPositionError) => {
      if (settled) return;
      finish();
      const code = typeof error?.code === 'number' ? error.code : null;
      const err = new GeoError(
        kindFromCode(code),
        code,
        error?.message || 'geolocation failed'
      );
      record({
        t: startedAt,
        source,
        durationMs: Date.now() - startedAt,
        ok: false,
        code,
        message: err.message,
        accuracy: null,
      });
      reject(err);
    };

    try {
      // ALWAYS explicit: relying on library/browser defaults is defect A.
      navigator.geolocation.getCurrentPosition(onSuccess, onError, {
        enableHighAccuracy: highAccuracy,
        timeout: timeoutMs,
        maximumAge: maxAgeMs,
      });
    } catch (e: any) {
      // a synchronous throw must still release the slot — otherwise it would be
      // exactly the latch this module exists to eliminate
      if (settled) return;
      finish();
      const err = new GeoError(
        'UNAVAILABLE',
        null,
        e?.message || 'getCurrentPosition threw synchronously'
      );
      record({
        t: startedAt,
        source,
        durationMs: Date.now() - startedAt,
        ok: false,
        code: null,
        message: err.message,
        accuracy: null,
      });
      reject(err);
    }
  });

  slot.promise = promise;
  return promise;
};

// ---------------------------------------------------------------------------
// continuous watch (follow mode)
//
// The old GeolocateControl was constructed with `trackUserLocation: true`, so a
// successful tap started a `watchPosition` and mapbox kept moving its dot,
// accuracy circle and the camera as the user walked. Those markers were added to
// the MAP, not to the control's hidden container, so `display:none` never
// disabled that behaviour — for users where Locate worked at all (notably mobile
// Safari) it was reachable and real. This is our replacement for it.
//
// It is deliberately GARNISH, never load-bearing. `watchPosition` is one of the
// primitives suspected of never invoking either callback in an iOS standalone
// PWA, which is the exact environment this ticket exists for, so:
//   - it is only ever started AFTER a one-shot fix has already been delivered;
//   - it never feeds `getPosition`, which keeps issuing its own
//     `getCurrentPosition` inside the tap's call stack regardless;
//   - it surfaces nothing to the user. Its errors go to the diagnostics ring
//     buffer (visible in the long-press debug panel) and nowhere else — a toast
//     per failed watch tick would be a stream of noise for a feature the user
//     never asked for;
//   - it sets no latch. A watch error may not degrade `getPosition` or a later
//     `startWatch`.
// A watch that stays silent forever therefore leaves the user with exactly the
// one-shot behaviour and no way to tell the difference.
// ---------------------------------------------------------------------------
/**
 * Built per `startWatch` call for the same reason as the error handler below:
 * the entry it writes carries the source the caller configured, so nothing here
 * can go stale against a later, differently-labelled watch.
 */
const makeWatchSuccessHandler =
  (source: string) =>
  (position: GeolocationPosition): void => {
    const p: GeoPosition = {
      lat: position.coords.latitude,
      lon: position.coords.longitude,
      // published so the map can size the accuracy circle around the dot
      accuracy: position.coords.accuracy,
      timestamp: position.timestamp || Date.now(),
    };

    // THE VERDICT, WITHOUT A ROW.
    //
    // A watch tick firing is the browser handing us a real fix, which is the
    // same class of evidence as a successful one-shot: permission is not
    // blocked NOW. Until round 5 only `makeWatchErrorHandler` touched the
    // verdict, so a watch could CONVICT but never ACQUIT — and that reopened
    // the exact failure this ticket exists to kill, by a second route. Deny ->
    // read the toast -> fix Settings -> return to the SAME live page, where the
    // watch that the denial deliberately left registered recovers and delivers
    // a fix. The panel went on saying "blocked" while the blue dot it drew was
    // moving: a false "blocked" told against evidence already in hand, the same
    // shape as the round-3 eviction defect.
    //
    // It must NOT push a ring-buffer entry, though: a walking user produces a
    // tick every few seconds and would evict all 20 Locate attempts within a
    // minute, destroying the one diagnostic that made this bug findable.
    // Before round 4 those two requirements were in direct conflict, because
    // the ring buffer WAS the verdict. Now that they are separate variables
    // both hold at once, and this is the path that needs the split.
    // `watching` in getDiagnostics() still reports liveness.
    //
    // Deliberately ABOVE the monotonic guard. That guard protects the shared
    // cache and the blue dot from being rewound by a late, stale fix; it says
    // nothing about permission. A stale tick still proves the browser answered
    // us just now, so it must still acquit. `t` is therefore the OBSERVATION
    // time and not `position.timestamp`, which may be a replayed older fix.
    recordVerdictOnly({
      t: Date.now(),
      source,
      durationMs: 0,
      ok: true,
      code: null,
      message: WATCH_FIX_MESSAGE,
      accuracy: p.accuracy,
    });

    // Same monotonic rule as `getPosition`: only ever ADVANCE the shared cache.
    // A watch tick and a one-shot can land in either order, and an older watch
    // tick must not rewind the blue dot to a staler fix.
    if (cachedPosition && p.timestamp < cachedPosition.timestamp) return;
    cachedPosition = p;
    notify(p);
  };

/**
 * Built per `startWatch` call so the caller's `source` reaches the ring buffer.
 *
 * It has to close over the source rather than read a module-level "current watch
 * source": an ASYNCHRONOUS watch error can land long after the call that
 * registered it, and the debug panel's whole job is to say WHICH feature was
 * asking. Hard-coding `'watch'` here made `startWatch({ source: 'follow' })`
 * silently ineffective for every normal (asynchronous) watch failure — only a
 * synchronous throw ever recorded the configured source.
 */
const makeWatchErrorHandler =
  (source: string) =>
  (error: GeolocationPositionError): void => {
    const code = typeof error?.code === 'number' ? error.code : null;
    record({
      t: Date.now(),
      source,
      durationMs: 0,
      ok: false,
      code,
      message: error?.message || 'watch failed',
      accuracy: null,
    });
    // Deliberately NO state change: we do not clear `watchId` (the OS watch is
    // still registered and may recover — a TIMEOUT indoors often does), and above
    // all we set no "watch failed" flag. That flag is exactly the latch class this
    // module exists to eliminate.
  };

/**
 * Start following the user's position. Best effort, idempotent, and safe to
 * call when geolocation is unavailable — it never throws and never rejects,
 * because no caller is allowed to depend on it (see rule 6).
 *
 * Updates flow through the normal `notify`/cache path, so every existing
 * `subscribe` consumer (the blue dot) follows along with no extra wiring.
 */
export const startWatch = (opts: StartWatchOptions = {}): void => {
  // idempotent: a second call must not leave a second OS watch running, which
  // would double the battery cost and leak on the next stopWatch
  if (watchId !== null) return;
  if (!hasGeolocation() || !isSecure()) return;
  if (typeof navigator.geolocation.watchPosition !== 'function') return;

  const {
    timeoutMs = DEFAULT_WATCH_TIMEOUT_MS,
    // 0: a follow mode wants live fixes, not a replay of an old one
    maxAgeMs = 0,
    highAccuracy = true,
    source = 'watch',
  } = opts;

  try {
    // ALWAYS explicit, same rule as getPosition: never inherit library defaults.
    const id = navigator.geolocation.watchPosition(
      makeWatchSuccessHandler(source),
      makeWatchErrorHandler(source),
      {
        enableHighAccuracy: highAccuracy,
        timeout: timeoutMs,
        maximumAge: maxAgeMs,
      }
    );
    watchId = typeof id === 'number' ? id : null;
  } catch (e: any) {
    // a synchronous throw is recorded and then forgotten — never latched
    watchId = null;
    record({
      t: Date.now(),
      source,
      durationMs: 0,
      ok: false,
      code: null,
      message: e?.message || 'watchPosition threw synchronously',
      accuracy: null,
    });
  }
};

/** Stop following. Safe to call when idle, and safe to call twice. */
export const stopWatch = (): void => {
  if (watchId === null) return;
  const id = watchId;
  // cleared FIRST: if clearWatch throws we must still be able to start a new
  // watch later, rather than being stuck believing one is already running
  watchId = null;
  try {
    navigator.geolocation.clearWatch(id);
  } catch {
    // best effort
  }
};

// ---------------------------------------------------------------------------
// diagnostics
// ---------------------------------------------------------------------------
export const getDiagnostics = (): GeoDiagnostics => ({
  entries: ringBuffer.slice(),
  lastPosition: cachedPosition,
  lastPositionAgeMs: cachedPosition
    ? Date.now() - cachedPosition.timestamp
    : null,
  hasGeolocation: hasGeolocation(),
  isSecureContext: isSecure(),
  inFlight: inFlight !== null,
  subscriberCount: subscribers.length,
  watching: watchId !== null,
});

// ---------------------------------------------------------------------------
// iOS lifecycle hardening: a standalone PWA can be resumed hours later with a
// long-stale cached fix still in memory.
// ---------------------------------------------------------------------------
const onResume = (): void => {
  if (!cachedPosition) return;
  if (Date.now() - cachedPosition.timestamp > STALE_ON_RESUME_MS) {
    invalidateCache();
  }
};

const onVisibilityChange = (): void => {
  if (typeof document === 'undefined') return;
  if (document.visibilityState === 'visible') onResume();
};

export const startLifecycleListeners = (): void => {
  if (lifecycleAttached) return;
  if (typeof document === 'undefined' || typeof window === 'undefined') return;
  document.addEventListener('visibilitychange', onVisibilityChange);
  window.addEventListener('pageshow', onResume);
  lifecycleAttached = true;
};

export const stopLifecycleListeners = (): void => {
  if (!lifecycleAttached) return;
  document.removeEventListener('visibilitychange', onVisibilityChange);
  window.removeEventListener('pageshow', onResume);
  lifecycleAttached = false;
};

/**
 * TEST-ONLY escape hatch. Clears every piece of module state (cache, in-flight
 * slot, subscribers, diagnostics ring buffer and its persisted copy), stops any
 * active watch and re-attaches the lifecycle listeners, so each test starts from
 * a clean module. Never call this from application code.
 *
 * `lastRealAttempt` IS cleared here, because it is ordinary module state that a
 * reset must not leak between tests — not a stand-in for a page load. Nothing
 * about the previous-session behaviour can be "proved" by calling this: the
 * tests that assert it (17m-17p, 17s) re-instantiate the module with
 * `jest.resetModules()`, which is the real thing rather than a simulation of it,
 * and the property they exercise is that a restored buffer never reaches this
 * variable at all.
 */
export const __resetForTests = (): void => {
  // first, so the OS watch is released rather than orphaned by the state reset
  stopWatch();
  cachedPosition = null;
  inFlight = null;
  subscribers = [];
  ringBuffer = [];
  lastRealAttempt = null;
  try {
    window.localStorage.removeItem(DIAGNOSTICS_STORAGE_KEY);
  } catch {
    // best effort
  }
  stopLifecycleListeners();
  startLifecycleListeners();
};

// Restores the DISPLAY history only. `lastRealAttempt` stays null, so nothing
// restored here can vote on the denial verdict — visible, but not authoritative.
// There is no ordering constraint any more: the round-3 "ORDER MATTERS" comment
// that used to sit here guarded a wall-clock session boundary that could not be
// made correct by ordering at all (see `lastRealAttempt`).
loadRingBuffer();
startLifecycleListeners();

const locationService = {
  getPosition,
  startWatch,
  stopWatch,
  getCached,
  invalidateCache,
  subscribe,
  getDiagnostics,
  describeGeoFailure,
  describeDenialEvidenceAge,
  formatDiagnosticTimestamp,
  isStandalone,
  isLocationDenied,
  getInferredDenialAt,
  startLifecycleListeners,
  stopLifecycleListeners,
  __resetForTests,
};

export default locationService;
